import type { GetServerSideProps } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { UAParser } from 'ua-parser-js';

import { H1, BodyRegular } from '@components/atoms/typography';
import {
  APP_STORE_URL,
  GOOGLE_PLAY_URL,
  STORE_BADGE_HEIGHT,
  STORE_BADGE_WIDTH,
  WEBSITE_NAME
} from '@lib/constants';

/**
 * Device-aware app download link: phones are redirected server-side
 * straight to their store, everything else (desktop, unknown agents)
 * gets both badges. This is the URL the funnel welcome QR code encodes,
 * so one code serves both stores.
 */
export const getServerSideProps: GetServerSideProps = async ({ req }) => {
  const osName = new UAParser(req.headers['user-agent']).getOS().name;

  if (osName === 'iOS') {
    return { redirect: { destination: APP_STORE_URL, permanent: false } };
  }

  if (osName === 'Android') {
    return { redirect: { destination: GOOGLE_PLAY_URL, permanent: false } };
  }

  return { props: {} };
};

const AppDownload = () => (
  <>
    <Head>
      <title>{`Get the app | ${WEBSITE_NAME}`}</title>
      <meta name="robots" content="noindex,nofollow" />
    </Head>
    <div className="flex flex-col items-center px-5 py-16">
      <H1 className="text-center">Get the Fantasy Football Hub app</H1>
      <BodyRegular className="mt-4 max-w-md text-center">
        Download the app on your phone to take your team with you.
      </BodyRegular>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
        <Link href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
          <Image
            src="/images/app-store-badge.png"
            alt="Download on the App Store"
            height={STORE_BADGE_HEIGHT}
            width={STORE_BADGE_WIDTH}
          />
        </Link>
        <Link href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer">
          <Image
            src="/images/google-play-badge.svg"
            alt="Get it on Google Play"
            height={STORE_BADGE_HEIGHT}
            width={STORE_BADGE_WIDTH}
          />
        </Link>
      </div>
    </div>
  </>
);

export default AppDownload;
